import { getContext, setContext } from 'svelte';

/**
 * 'tree' is the vertical tree with open/close buttons (sidebar and mobile menu),
 * 'bar' the horizontal strip of level 1 with flyouts.
 */
export type NavigationVariant = 'tree' | 'bar';

export interface VariantContext {
    variant: NavigationVariant;
}

/**
 * Not a service: the variant lives in the Svelte context and so belongs to a place in the component tree.
 * getContext()/setContext() only work while a component initialises — create the handler there.
 */
export class NavigationHandler {
    private static readonly ContextKey = 'navigation-variant';
    private static readonly DefaultVariant: NavigationVariant = 'tree';

    public getVariant(): NavigationVariant {
        const SearchContext = getContext<(() => VariantContext | undefined) | undefined>(NavigationHandler.ContextKey);

        if (!SearchContext) {
            return NavigationHandler.DefaultVariant;
        }

        return SearchContext()?.variant ?? NavigationHandler.DefaultVariant;
    }

    public setVariant(variant: NavigationVariant): void {
        const WriteContext: VariantContext = { variant };
        setContext(NavigationHandler.ContextKey, () => WriteContext);
    }

    /**
     * Counterpart of LayerHandler.upNavigationLayer(): level 1 sets the variant,
     * nested navigations inherit it without overriding it.
     */
    public resolveVariant(variant: NavigationVariant | undefined): NavigationVariant {
        const NextVariant = variant ?? this.getVariant();
        this.setVariant(NextVariant);

        return NextVariant;
    }
}
